import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { WalletContext } from '../../context/WalletContext';
import WalletPickerModal from '../WalletPickerModal';
import {
  CANBORSA_CHAIN_ID, CANBORSA_TOKENS, CANBORSA_MARKETS,
  CANBORSA_MIN_POSITION_USD, CANBORSA_EXECUTION_FEE_FALLBACK_WEI,
  MARKET_CATEGORIES, COLLATERAL_OPTIONS,
} from './canborsaConstants';
import {
  ERC20_ABI,
  getCollateralBalance, ensureCollateralApproved,
  openCanborsaPosition, closeCanborsaPosition,
  fetchCanborsaPositions, fetchCanborsaOraclePrice,
  fetchCanborsaExecutionFee, switchToArbitrum,
} from './canborsaUtils';
import axios from 'axios';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const ANALYTICS_TTL = 2 * 60 * 1000;
const LEVERAGES = [2, 5, 10, 20, 30];

// ── shared style helpers (same as GmxPanel) ───────────────────────────────────
function modeColor(mode, isHold) {
  if (isHold) return '#f7c948';
  if (mode === 'buy')  return '#26c49a';
  if (mode === 'sell') return '#c0392b';
  return '#555';
}
function modeFrame(mode, isHold) {
  if (isHold)          return { bg: 'rgba(247,201,72,0.10)',  border: 'rgba(247,201,72,0.45)' };
  if (mode === 'buy')  return { bg: 'rgba(38,196,154,0.10)',  border: 'rgba(38,196,154,0.45)' };
  if (mode === 'sell') return { bg: 'rgba(192,57,43,0.10)',   border: 'rgba(192,57,43,0.45)'  };
  return               { bg: 'rgba(85,85,85,0.08)',           border: 'rgba(85,85,85,0.25)'   };
}

// ── RWA price feed ────────────────────────────────────────────────────────────
// Crypto markets use Binance WS. Equity/commodity markets need a REST price source.
// TODO: wire Polygon.io or Pyth for AAPL, NVDA, TSLA, XAU once an API key is set up.
async function fetchRwaPrice(rfId) { // eslint-disable-line no-unused-vars
  return null; // placeholder — return null means "no price yet"
}

export default function CanborsaPanel({ market, setMarket }) {
  const { connected, address, chainId, signer, provider, disconnectWallet } = useContext(WalletContext);

  const [side,          setSide]          = useState('long');
  const [leverage,      setLeverage]      = useState(2);
  const [collateral,    setCollateral]    = useState('');
  const [collSymbol,    setCollSymbol]    = useState('USDC');
  const [prices,        setPrices]        = useState({});
  const [collBal,       setCollBal]       = useState(null);
  const [ethBal,        setEthBal]        = useState(null);
  const [balLoading,    setBalLoading]    = useState(false);
  const [allowance,     setAllowance]     = useState(null);
  const [approving,     setApproving]     = useState(false);
  const [status,        setStatus]        = useState('');
  const [loading,       setLoading]       = useState(false);
  const [txHash,        setTxHash]        = useState(null);
  const [positions,     setPositions]     = useState([]);
  const [posLoading,    setPosLoading]    = useState(false);
  const [pendingOrder,  setPendingOrder]  = useState(null);
  const [pendingClose,  setPendingClose]  = useState(null);
  const [closeStatus,   setCloseStatus]   = useState('');
  const [analytics,     setAnalytics]     = useState(null);
  const [showPicker,    setShowPicker]    = useState(false);
  const [activeCat,     setActiveCat]     = useState('crypto');

  const analyticsTsRef = useRef(0);
  const wsRef          = useRef(null);
  const wsReconnRef    = useRef(null);
  const wsMountedRef   = useRef(true);
  const keeperPollRef  = useRef(null);

  const onArbitrum  = chainId === CANBORSA_CHAIN_ID;
  const mkt         = CANBORSA_MARKETS[market] || CANBORSA_MARKETS['BTC/USD'];
  const price       = prices[mkt.cgId || market] || 0;
  const collAmt     = parseFloat(collateral) || 0;
  const sizeUsd     = collAmt * leverage;
  const liqEst      = side === 'long'
    ? price * (1 - (0.9 / leverage))
    : price * (1 + (0.9 / leverage));

  const marketsByCategory = MARKET_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = Object.keys(CANBORSA_MARKETS).filter(k => CANBORSA_MARKETS[k].category === cat);
    return acc;
  }, {});

  // ── analytics (R values) ───────────────────────────────────────────────────
  const fetchAnalytics = useCallback(async () => {
    if (Date.now() - analyticsTsRef.current < ANALYTICS_TTL && analytics) return;
    try {
      const res = await axios.get(`${API_BASE}/btc_today`, {
        timeout: 10000,
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      setAnalytics({ rc: res.data.r_current, vp: res.data.volume_profile });
      analyticsTsRef.current = Date.now();
    } catch { /* silent */ }
  }, [analytics]);

  useEffect(() => {
    fetchAnalytics();
    const t = setInterval(fetchAnalytics, ANALYTICS_TTL);
    return () => clearInterval(t);
  }, [fetchAnalytics]);

  // ── prices: Binance WS for crypto, REST fallback ──────────────────────────
  useEffect(() => {
    wsMountedRef.current = true;
    let wsFails = 0;
    let restPoll = null;

    const fetchRest = async () => {
      try {
        const [btcRes, ethRes] = await Promise.all([
          axios.get(`${API_BASE}/proxy/ticker?symbol=BTCUSDT`, { timeout: 6000 }),
          axios.get(`${API_BASE}/proxy/ticker?symbol=ETHUSDT`, { timeout: 6000 }),
        ]);
        const btc = parseFloat(btcRes.data.lastPrice || btcRes.data.price);
        const eth = parseFloat(ethRes.data.lastPrice || ethRes.data.price);
        if (btc) setPrices(p => ({ ...p, bitcoin:  p.bitcoin  || btc }));
        if (eth) setPrices(p => ({ ...p, ethereum: p.ethereum || eth }));
      } catch {}
    };

    fetchRest();

    function connect() {
      if (!wsMountedRef.current) return;
      if (wsFails >= 3) {
        if (!restPoll) {
          restPoll = setInterval(() => {
            axios.get(`${API_BASE}/proxy/ticker?symbol=BTCUSDT`, { timeout: 6000 })
              .then(r => { const p = parseFloat(r.data.lastPrice || r.data.price); if (p) setPrices(prev => ({ ...prev, bitcoin: p })); }).catch(() => {});
            axios.get(`${API_BASE}/proxy/ticker?symbol=ETHUSDT`, { timeout: 6000 })
              .then(r => { const p = parseFloat(r.data.lastPrice || r.data.price); if (p) setPrices(prev => ({ ...prev, ethereum: p })); }).catch(() => {});
          }, 10_000);
        }
        return;
      }
      const ws = new WebSocket('wss://stream.binance.com:9443/stream?streams=btcusdt@ticker/ethusdt@ticker');
      wsRef.current = ws;
      ws.onmessage = e => {
        try {
          const { stream, data } = JSON.parse(e.data);
          const p = parseFloat(data.c);
          if (!p) return;
          if (stream.startsWith('btcusdt'))      setPrices(prev => ({ ...prev, bitcoin:  p }));
          else if (stream.startsWith('ethusdt')) setPrices(prev => ({ ...prev, ethereum: p }));
        } catch {}
      };
      ws.onclose = () => { wsRef.current = null; wsFails++; if (wsMountedRef.current) wsReconnRef.current = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      wsMountedRef.current = false;
      clearTimeout(wsReconnRef.current);
      clearInterval(restPoll);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── collateral balance ────────────────────────────────────────────────────
  const refreshBal = useCallback(async () => {
    if (!connected || !address || !onArbitrum) return;
    setBalLoading(true);
    try {
      const prov = provider || signer?.provider;
      const b = await getCollateralBalance(prov, address, collSymbol);
      setCollBal(b);
    } catch { setCollBal(0); }
    finally { setBalLoading(false); }
  }, [connected, address, onArbitrum, provider, signer, collSymbol]);

  useEffect(() => {
    refreshBal();
    const t = setInterval(refreshBal, 30_000);
    return () => clearInterval(t);
  }, [refreshBal]);

  // ── allowance + ETH balance ───────────────────────────────────────────────
  const checkAllowance = useCallback(async () => {
    if (!connected || !address || !onArbitrum) return;
    const prov = provider || signer?.provider;
    try { const r = await prov.getBalance(address); setEthBal(parseFloat(ethers.utils.formatEther(r))); } catch {}
    // TODO: router address required — until then show allowance as max so approval isn't prompted
    if (!require('./canborsaConstants').CANBORSA_CONTRACTS.Router) {
      setAllowance(ethers.constants.MaxUint256);
      return;
    }
    try {
      const token = CANBORSA_TOKENS[collSymbol];
      const c = new ethers.Contract(token.address, ERC20_ABI, prov);
      const al = await c.allowance(address, require('./canborsaConstants').CANBORSA_CONTRACTS.Router);
      setAllowance(al);
    } catch { setAllowance(ethers.BigNumber.from(0)); }
  }, [connected, address, onArbitrum, provider, signer, collSymbol]);

  useEffect(() => {
    checkAllowance();
    const t = setInterval(checkAllowance, 30_000);
    return () => clearInterval(t);
  }, [checkAllowance]);

  const approveCollateral = async () => {
    if (collAmt <= 0) { setStatus('Enter collateral amount first, then approve'); return; }
    setApproving(true);
    setStatus('Check wallet — approve collateral…');
    try {
      const token = CANBORSA_TOKENS[collSymbol];
      const amountRaw = ethers.utils.parseUnits(collAmt.toFixed(token.decimals), token.decimals);
      await ensureCollateralApproved(signer, amountRaw, collSymbol);
      await checkAllowance();
      setStatus('Approved. Ready to trade.');
    } catch (err) {
      setStatus(err?.message?.slice(0, 80) || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  // ── positions ─────────────────────────────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    if (!connected || !address || !onArbitrum) return;
    setPosLoading(true);
    try {
      const prov = provider || signer?.provider;
      const pos = await fetchCanborsaPositions(prov, address);
      setPositions(pos);
    } catch { /* silent until contracts wired */ }
    finally { setPosLoading(false); }
  }, [connected, address, onArbitrum, provider, signer]);

  useEffect(() => {
    fetchPositions();
    const t = setInterval(fetchPositions, 30_000);
    return () => { clearInterval(t); if (keeperPollRef.current) clearInterval(keeperPollRef.current); };
  }, [fetchPositions]);

  // ── poll positions after order ────────────────────────────────────────────
  const pollUntilPosition = (prevCount, setStatusFn) => {
    if (keeperPollRef.current) clearInterval(keeperPollRef.current);
    let attempts = 0;
    keeperPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const prov = provider || signer?.provider;
        const pos = await fetchCanborsaPositions(prov, address);
        setPositions(pos);
        if (pos.length !== prevCount || attempts >= 36) {
          clearInterval(keeperPollRef.current);
          keeperPollRef.current = null;
          if (pos.length !== prevCount) setStatusFn('');
          else setStatusFn('Position not yet visible — refresh manually if needed');
        }
      } catch {}
    }, 5000);
  };

  // ── execute ───────────────────────────────────────────────────────────────
  const execute = async () => {
    if (collAmt <= 0)                          { setStatus('Enter collateral amount'); return; }
    if (balLoading)                            { setStatus('Fetching balance… try again'); return; }
    if (collBal !== null && collAmt > collBal + 0.01) {
      setStatus(`Insufficient ${collSymbol} — wallet shows $${collBal.toFixed(2)}`);
      return;
    }
    if (price <= 0)                            { setStatus('Price not available — wait a moment'); return; }
    if (sizeUsd < CANBORSA_MIN_POSITION_USD)   { setStatus(`Min position $${CANBORSA_MIN_POSITION_USD} — increase collateral or leverage`); return; }

    // Try to get on-chain oracle price to verify against CG/Binance
    const oraclePrice = await fetchCanborsaOraclePrice(provider || signer?.provider, mkt.marketToken);
    if (oraclePrice) {
      const delta = Math.abs(price - oraclePrice) / oraclePrice;
      if (delta > 0.02) {
        setPendingOrder({ collAmt, sizeUsd, leverage, side, execPrice: price, liqEst,
          warn: `Price divergence: feed $${Math.round(price).toLocaleString()} vs oracle $${Math.round(oraclePrice).toLocaleString()} (${(delta*100).toFixed(1)}%) — wait for sync`,
          blocking: true });
        return;
      }
    }

    setPendingOrder({ collAmt, sizeUsd, leverage, side, execPrice: price, liqEst, warn: null, blocking: false });
    setStatus('');
  };

  const executeConfirmed = async () => {
    const po = pendingOrder;
    setPendingOrder(null);
    setLoading(true);
    try {
      const token = CANBORSA_TOKENS[collSymbol];
      const collateralRaw = ethers.utils.parseUnits(po.collAmt.toFixed(token.decimals), token.decimals);

      setStatus('Checking allowance…');
      await ensureCollateralApproved(signer, collateralRaw, collSymbol);

      setStatus('Check wallet — confirm order tx…');
      const executionFeeWei = await fetchCanborsaExecutionFee(signer.provider);

      const tx = await openCanborsaPosition({
        signer,
        marketToken:         mkt.marketToken,
        collateralToken:     token.address,
        collateralAmountRaw: collateralRaw,
        isLong:              po.side === 'long',
        leverage:            po.leverage,
        executionFeeWei,
      });

      setTxHash(tx.hash);
      setStatus('Submitted — waiting for confirmation…');

      const receipt = await tx.wait(1);
      if (receipt.status === 0) { setStatus('Transaction reverted — see Arbiscan link below'); return; }

      setCollateral('');
      refreshBal();
      const prevCount = positions.length;
      setStatus('Confirmed — waiting for position to appear…');
      pollUntilPosition(prevCount, setStatus);
    } catch (err) {
      setStatus(err?.message?.slice(0, 80) || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  const executeClose = async () => {
    const pos = pendingClose;
    setPendingClose(null);
    setCloseStatus('Closing position…');
    try {
      const executionFeeWei = await fetchCanborsaExecutionFee(signer.provider);
      const tx = await closeCanborsaPosition({ signer, positionKey: pos.key, executionFeeWei });
      setTxHash(tx.hash);
      setCloseStatus('Submitted — waiting for confirmation…');
      const receipt = await tx.wait(1);
      if (receipt.status === 0) { setCloseStatus('Transaction reverted — see Arbiscan link'); return; }
      refreshBal();
      const prevCount = positions.length;
      setCloseStatus('Confirmed — updating positions…');
      pollUntilPosition(prevCount, setCloseStatus);
    } catch (err) {
      setCloseStatus(err?.message?.slice(0, 80) || 'Close failed');
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  const contractsReady = !!require('./canborsaConstants').CANBORSA_CONTRACTS.Router;
  const needsApproval  = contractsReady && allowance !== null && collAmt > 0 &&
    allowance.lt(ethers.utils.parseUnits(collAmt.toFixed(6), 6));

  return (
    <div className="tc-gmx-panel">

      {/* Wallet bar */}
      {connected ? (
        <div className="tc-wallet-bar">
          <span className="tc-wallet-addr">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
          <button className="tc-wallet-disconnect" onClick={disconnectWallet}>DISCONNECT</button>
        </div>
      ) : (
        <div className="tc-wallet-bar tc-wallet-bar--disconnected">
          <button className="tc-wallet-connect-btn" onClick={() => setShowPicker(true)}>
            CONNECT WALLET
          </button>
          <WalletPickerModal
            open={showPicker}
            onClose={() => setShowPicker(false)}
            onConnected={() => setShowPicker(false)}
          />
        </div>
      )}

      {/* Contracts-not-yet-wired notice */}
      {!contractsReady && (
        <div style={{
          textAlign: 'center', fontSize: 8, letterSpacing: '1px',
          color: '#f7c948', background: 'rgba(247,201,72,0.08)',
          border: '1px solid rgba(247,201,72,0.30)', borderRadius: 2,
          padding: '3px 6px', marginBottom: 8,
        }}>
          CANBORSA — CONTRACTS PENDING DISCOVERY
        </div>
      )}

      {/* Category tabs */}
      <div className="tc-row" style={{ gap: 4, marginBottom: 6 }}>
        {MARKET_CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`tc-lev-tab ${activeCat === cat ? 'active' : ''}`}
            onClick={() => {
              setActiveCat(cat);
              const firstInCat = marketsByCategory[cat]?.[0];
              if (firstInCat) setMarket(firstInCat);
            }}
            style={{ flex: 1, textTransform: 'uppercase', fontSize: 8 }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Market selector */}
      <div className="tc-row">
        <span className="tc-label">MARKET</span>
        <div className="tc-market-tabs">
          {(marketsByCategory[activeCat] || []).map(k => (
            <button
              key={k}
              className={`tc-market-tab ${market === k ? 'active' : ''}`}
              onClick={() => setMarket(k)}
            >
              {CANBORSA_MARKETS[k].symbol}
            </button>
          ))}
        </div>
      </div>

      {/* Live price */}
      <div className="tc-row tc-price-row">
        <span className="tc-label">PRICE</span>
        <span className="tc-price-value">
          {price > 0
            ? `$${price.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}`
            : mkt.category !== 'crypto' ? 'RWA FEED TODO' : '—'}
        </span>
      </div>

      {/* Analytics (BTC only — same backend) */}
      {mkt.symbol === 'BTC' && analytics?.rc && (<>
        <div className="tc-row tc-summary-row">
          <span className="tc-label">R 4H</span>
          {(() => { const f = modeFrame(analytics.rc.mode_4h, analytics.rc.hold_4h); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_4h, analytics.rc.hold_4h),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>{analytics.rc.r_4h >= 0 ? '+' : ''}{analytics.rc.r_4h?.toFixed(2) ?? '—'}</span>
          ); })()}
        </div>
        <div className="tc-row tc-summary-row" style={{ marginBottom: 14 }}>
          <span className="tc-label">R DAILY</span>
          {(() => { const f = modeFrame(analytics.rc.mode_daily, analytics.rc.hold_daily); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_daily, analytics.rc.hold_daily),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>{analytics.rc.r_daily >= 0 ? '+' : ''}{analytics.rc.r_daily?.toFixed(2) ?? '—'}</span>
          ); })()}
        </div>
      </>)}

      {/* Long / Short */}
      <div className="tc-row">
        <button className={`tc-side-btn long  ${side === 'long'  ? 'active' : ''}`} onClick={() => setSide('long')}>LONG</button>
        <button className={`tc-side-btn short ${side === 'short' ? 'active' : ''}`} onClick={() => setSide('short')}>SHORT</button>
      </div>

      {/* Collateral token selector */}
      <div className="tc-row">
        <span className="tc-label">COLLATERAL</span>
        <div className="tc-lev-tabs">
          {COLLATERAL_OPTIONS.map(sym => (
            <button
              key={sym}
              className={`tc-lev-tab ${collSymbol === sym ? 'active' : ''}`}
              onClick={() => { setCollSymbol(sym); setCollateral(''); }}
            >{sym}</button>
          ))}
        </div>
      </div>

      {/* Collateral input */}
      <div className="tc-row tc-input-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span className="tc-label">AMOUNT</span>
          {collBal !== null && collBal > 0 && (
            <span style={{ fontSize: 8, color: '#444', letterSpacing: '0.5px' }}>
              BAL <span style={{ color: '#666' }}>${collBal.toFixed(2)}</span>
            </span>
          )}
        </div>
        <div className="tc-input-group">
          <input
            type="number" className="tc-input" placeholder="0.00"
            value={collateral} onChange={e => setCollateral(e.target.value)} min="0"
          />
          <span className="tc-input-suffix">{collSymbol}</span>
        </div>
        {collBal > 0 && (
          <div style={{ width: '100%', marginTop: 6 }}>
            <input
              type="range" min={0} max={100} step={1}
              value={collBal > 0 ? Math.round((collAmt / collBal) * 100) : 0}
              onChange={e => {
                const pct = parseFloat(e.target.value);
                const amt = Math.floor(collBal * pct) / 100;
                setCollateral(amt > 0 ? amt.toFixed(2) : '');
              }}
              style={{ width: '100%', accentColor: '#26c49a', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              {[0, 25, 50, 75, 100].map(pct => (
                <button
                  key={pct}
                  onClick={() => { const amt = Math.floor(collBal * pct) / 100; setCollateral(amt > 0 ? amt.toFixed(2) : ''); }}
                  style={{
                    background: 'none', border: 'none', padding: '1px 0',
                    fontSize: 8, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.3px',
                    color: Math.round((collAmt / collBal) * 100) === pct ? '#26c49a' : '#444',
                  }}
                >{pct}%</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Leverage */}
      <div className="tc-row">
        <span className="tc-label">LEVERAGE</span>
        <div className="tc-lev-tabs">
          {LEVERAGES.filter(l => l <= mkt.maxLeverage).map(l => (
            <button
              key={l}
              className={`tc-lev-tab ${leverage === l ? 'active' : ''}`}
              onClick={() => setLeverage(l)}
            >{l}×</button>
          ))}
        </div>
      </div>

      {/* Size summary */}
      <div className="tc-row tc-summary-row">
        <span className="tc-label">SIZE</span>
        <span className="tc-summary-val">
          ${sizeUsd > 0 ? sizeUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
        </span>
      </div>

      {/* Liquidation estimate */}
      {price > 0 && collAmt > 0 && (
        <div className="tc-row tc-summary-row">
          <span className="tc-label">LIQ. EST. <span style={{ fontSize: 7, color: '#444' }}>(excl. fees)</span></span>
          <span className="tc-summary-val tc-liq">
            ~${liqEst.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}
          </span>
        </div>
      )}

      {/* Execute / chain switch / approve */}
      {!connected ? (
        <button className="tc-execute-btn long" disabled style={{ opacity: 0.3 }}>CONNECT WALLET ABOVE</button>
      ) : !onArbitrum ? (
        <button className="tc-execute-btn long" onClick={switchToArbitrum}>SWITCH TO ARBITRUM</button>
      ) : needsApproval ? (
        <>
          <button className="tc-execute-btn long" onClick={approveCollateral} disabled={approving}>
            {approving ? 'APPROVING…' : `APPROVE ${collSymbol}`}
          </button>
          {status && <div className="tc-status">{status}</div>}
        </>
      ) : (
        <>
          <button
            className={`tc-execute-btn ${side}`}
            onClick={execute}
            disabled={loading || collAmt <= 0 || !contractsReady}
            title={!contractsReady ? 'Contracts not yet wired — devtools inspection required' : ''}
          >
            {loading ? 'SENDING…' : `${side === 'long' ? 'LONG' : 'SHORT'} ${mkt.symbol}`}
          </button>
          {status && <div className="tc-status">{status}</div>}
          {txHash && (
            <a href={`https://arbiscan.io/tx/${txHash}`} target="_blank" rel="noopener noreferrer" className="tc-arbiscan-link">
              ↗ Arbiscan
            </a>
          )}
          <div className="tc-funds">
            <span className="tc-label">
              MY FUNDS{' '}
              <span
                style={{ cursor: 'pointer', color: balLoading ? '#555' : '#0ef8f8', fontSize: 9 }}
                onClick={() => { refreshBal(); checkAllowance(); }}
                title="Refresh balance"
              >↻</span>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <span className="tc-funds-val">
                {balLoading ? '…' : collBal === null ? '—' : `$${collBal.toFixed(2)} ${collSymbol}`}
              </span>
              <span className="tc-funds-val" style={{ color: ethBal !== null && ethBal < 0.0003 ? '#c0392b' : '#888', fontSize: 9 }}>
                {ethBal === null ? 'GAS —' : `GAS ${ethBal.toFixed(5)} ETH`}
              </span>
            </span>
          </div>
        </>
      )}

      {/* Open positions */}
      {connected && onArbitrum && (
        <div className="tc-positions">
          <div className="tc-positions-header">
            <span className="tc-label">POSITIONS</span>
            <span className="tc-positions-refresh" onClick={fetchPositions} title="Refresh">↻</span>
          </div>
          {closeStatus && <div className="tc-status" style={{ margin: '0 0 4px' }}>{closeStatus}</div>}
          {txHash && (
            <a href={`https://arbiscan.io/tx/${txHash}`} target="_blank" rel="noopener noreferrer"
              className="tc-arbiscan-link" style={{ display: 'block', marginBottom: 8 }}>↗ Arbiscan</a>
          )}
          {posLoading && positions.length === 0 ? (
            <div className="tc-positions-empty">loading…</div>
          ) : positions.length === 0 ? (
            <div className="tc-positions-empty">
              {contractsReady ? 'no open positions' : 'contracts pending — positions unavailable'}
            </div>
          ) : positions.map(pos => {
            const pnl = pos.entryPrice > 0 && price > 0
              ? (pos.isLong
                  ? (price - pos.entryPrice) / pos.entryPrice * pos.sizeUsd
                  : (pos.entryPrice - price) / pos.entryPrice * pos.sizeUsd)
              : null;
            const pnlPct = pos.collateralUsd > 0 && pnl !== null ? pnl / pos.collateralUsd * 100 : null;
            return (
              <div key={pos.key} className="tc-position-card">
                <div className="tc-position-top">
                  <span className={`tc-position-side ${pos.isLong ? 'long' : 'short'}`}>
                    {pos.marketKey} · {pos.isLong ? 'LONG' : 'SHORT'} {pos.leverage}×
                  </span>
                  <button className="tc-close-btn" onClick={() => setPendingClose(pos)}>CLOSE</button>
                </div>
                <div className="tc-position-stats">
                  <span><span className="tc-label">SIZE </span>
                    <span className="tc-position-val">${pos.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </span>
                  <span><span className="tc-label">ENTRY </span>
                    <span className="tc-position-val">${pos.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </span>
                  {pnl !== null && (
                    <span><span className="tc-label">PNL </span>
                      <span className={`tc-position-pnl ${pnl >= 0 ? 'pos' : 'neg'}`}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct?.toFixed(1)}%)
                      </span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order confirmation modal */}
      {pendingOrder && (
        <div className="tc-modal-overlay" onClick={() => setPendingOrder(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM ORDER</div>
            {pendingOrder.warn && <div className="tc-modal-warn">{pendingOrder.warn}</div>}
            <div className="tc-modal-row"><span>VENUE</span><span style={{ color: '#0ef8f8' }}>CANBORSA</span></div>
            <div className="tc-modal-row"><span>MARKET</span><span>{market}</span></div>
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingOrder.side === 'long' ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingOrder.side.toUpperCase()}
              </span>
            </div>
            <div className="tc-modal-row"><span>COLLATERAL</span><span>${pendingOrder.collAmt.toFixed(2)} {collSymbol}</span></div>
            <div className="tc-modal-row"><span>LEVERAGE</span><span>{pendingOrder.leverage}×</span></div>
            <div className="tc-modal-row"><span>SIZE</span><span>${pendingOrder.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
            <div className="tc-modal-row"><span>ENTRY</span><span>${pendingOrder.execPrice.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}</span></div>
            <div className="tc-modal-row">
              <span>LIQ. EST.</span>
              <span style={{ color: '#e67e22' }}>
                ~${pendingOrder.liqEst.toLocaleString(undefined, { minimumFractionDigits: mkt.pricePrecision, maximumFractionDigits: mkt.pricePrecision })}
              </span>
            </div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingOrder(null)}>CANCEL</button>
              <button className={`tc-modal-confirm ${pendingOrder.side}`} onClick={executeConfirmed} disabled={pendingOrder.blocking}>CONFIRM</button>
            </div>
          </div>
        </div>
      )}

      {/* Close confirmation modal */}
      {pendingClose && (
        <div className="tc-modal-overlay" onClick={() => setPendingClose(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM CLOSE</div>
            <div className="tc-modal-row"><span>MARKET</span><span>{pendingClose.marketKey}</span></div>
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingClose.isLong ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingClose.isLong ? 'LONG' : 'SHORT'}
              </span>
            </div>
            <div className="tc-modal-row"><span>SIZE</span><span>${pendingClose.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
            <div className="tc-modal-row"><span>ENTRY</span><span>${pendingClose.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
            <div className="tc-modal-row"><span>CURRENT</span><span>{price > 0 ? `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</span></div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingClose(null)}>CANCEL</button>
              <button className={`tc-modal-confirm ${pendingClose.isLong ? 'short' : 'long'}`} onClick={executeClose}>CLOSE POSITION</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
